import { useEffect, useState } from 'react';
import { TableActions } from 'components/Table';
import { Delete, Email, Edit, Link as LinkIcon } from '@mui/icons-material';
import api from 'lib/trpcClient';
import useHandleTrpcMutation from 'hooks/ui/useHandleTrpcMutation';
import { useDisclosure } from 'further-ui/hooks';
import AlertDialog from 'components/AlertDialog';
import { AppTextInput } from 'components/FormElements';
import { Typography } from '@mui/material';
import Spacing from 'components/Spacing';
import { z } from 'zod';
import { FirmSelector } from 'components/FirmSelector';
import { RolePermission } from 'further-types/admin-user-role';
import { useHasPermissions } from 'hooks/ui/usePermissions';

type Props = {
  furtherUser: {
    furtherUserId: string;
    email: string;
    isDeletable: boolean;
  };
};

const ActionsColumn: React.FC<Props> = ({ furtherUser }) => {
  const deleteDisclosure = useDisclosure();
  const [passwordResetLink, setPasswordResetLink] = useState<
    string | undefined
  >();
  const selectFirmForPasswordResetDisclosure = useDisclosure<{
    type: 'email' | 'link';
  }>();
  const updateDisclosure = useDisclosure();
  const [newEmail, setNewEmail] = useState(furtherUser.email);
  const [newEmailError, setNewEmailError] = useState<string>('');
  const [selectedFirmIdForInvite, setSelectedFirmIdForInvite] = useState<
    string | undefined
  >();
  const [canReadFurtherUserPasswordResetLink] = useHasPermissions([
    RolePermission.ReadFurtherUserPasswordResetLink,
  ]);

  useEffect(() => {
    setNewEmail(furtherUser.email);
  }, [furtherUser]);

  const { mutate: deleteFurtherUser, isPending: isDeleting } =
    api.furtherUser.remove.useMutation(
      useHandleTrpcMutation()({
        successMessage: 'User deleted successfully.',
        onSuccess: () => {
          deleteDisclosure.onClose();
        },
      }),
    );
  const { mutate: updateFurtherUser, isPending: isUpdating } =
    api.furtherUser.update.useMutation(
      useHandleTrpcMutation()({
        successMessage: 'User updated successfully.',
        onSuccess: () => {
          updateDisclosure.onClose();
          setNewEmailError('');
        },
        onError: (error) => {
          if (error.data?.cause === 'This email is already in use.') {
            error.data.cause += ' Please add them as a new user instead.';
          }
        },
      }),
    );
  const {
    mutate: sendPasswordResetEmail,
    isPending: isSendingPasswordResetEmail,
  } = api.furtherUser.sendPasswordResetEmail.useMutation(
    useHandleTrpcMutation()({
      successMessage: 'Password reset email sent successfully.',
      onSuccess: () => {
        selectFirmForPasswordResetDisclosure.onClose();
        setSelectedFirmIdForInvite(undefined);
      },
    }),
  );
  const {
    mutate: getPasswordResetLink,
    isPending: isGettingPasswordResetLink,
  } = api.furtherUser.getPasswordResetLink.useMutation(
    useHandleTrpcMutation<{ link: string }>()({
      successMessage: 'Password reset link retrieved successfully.',
      invalidations: [],
      onSuccess: (data) => {
        setPasswordResetLink(data.link);
      },
    }),
  );

  return (
    <>
      <TableActions
        showAsDropdown
        actions={[
          {
            label: 'Delete',
            icon: Delete,
            color: 'error',
            disabled: !furtherUser.isDeletable,
            onClick: () => deleteDisclosure.onOpen(),
          },
          {
            icon: Email,
            label: 'Send password reset',
            onClick: () =>
              selectFirmForPasswordResetDisclosure.stageAction({
                type: 'email',
              }),
            color: 'primary',
            disabled: isSendingPasswordResetEmail,
          },
          {
            icon: LinkIcon,
            label: 'Get password reset link',
            onClick: () =>
              selectFirmForPasswordResetDisclosure.stageAction({
                type: 'link',
              }),
            color: 'primary',
            disabled: isSendingPasswordResetEmail,
            visible: canReadFurtherUserPasswordResetLink,
          },
          {
            icon: Edit,
            label: 'Edit email',
            onClick: () => updateDisclosure.onOpen(),
            color: 'primary',
          },
        ]}
      />

      <AlertDialog
        open={deleteDisclosure.isOpen}
        onClose={deleteDisclosure.onClose}
        onConfirm={() => {
          deleteFurtherUser({ furtherUserId: furtherUser.furtherUserId });
        }}
        title="Delete this user?"
        content="This will stop them from being able to log in, and remove them from the user list of investors they have access to."
        btnLabels={{
          cancel: 'Go back',
          confirm: 'Delete',
        }}
        confirmBtnProps={{ loading: isDeleting }}
      />

      <AlertDialog
        open={selectFirmForPasswordResetDisclosure.isOpen}
        onClose={selectFirmForPasswordResetDisclosure.onClose}
        onConfirm={() => {
          if (!selectedFirmIdForInvite) {
            return;
          }
          const input = {
            furtherUserId: furtherUser.furtherUserId,
            firmId: selectedFirmIdForInvite,
          };
          if (
            selectFirmForPasswordResetDisclosure.actionProps.type === 'email'
          ) {
            sendPasswordResetEmail(input);
          } else if (
            selectFirmForPasswordResetDisclosure.actionProps.type === 'link'
          ) {
            getPasswordResetLink(input);
          }
        }}
        icon={
          selectFirmForPasswordResetDisclosure.actionProps.type === 'email'
            ? Email
            : LinkIcon
        }
        iconColor="primary"
        title={
          selectFirmForPasswordResetDisclosure.actionProps.type === 'email'
            ? 'Select firm to send the email from'
            : 'Select firm to get password reset link for'
        }
        content={
          <Spacing size="md">
            <FirmSelector
              firmId={selectedFirmIdForInvite}
              onChange={(firmId) => {
                setSelectedFirmIdForInvite(firmId);
              }}
            />
            {selectFirmForPasswordResetDisclosure.actionProps.type ===
              'link' && (
              <Typography variant="body1">
                {passwordResetLink ??
                  'The password reset link will be shown here after clicking "Go"'}
              </Typography>
            )}
            {selectFirmForPasswordResetDisclosure.actionProps.type ===
              'email' && (
              <Typography variant="body1">
                The password reset email will be sent to the user's email
                address.
              </Typography>
            )}
          </Spacing>
        }
        btnLabels={{
          cancel: 'Go back',
          confirm: 'Go',
        }}
        confirmBtnProps={{
          disabled: !selectedFirmIdForInvite,
          loading: isSendingPasswordResetEmail || isGettingPasswordResetLink,
        }}
      />

      <AlertDialog
        size="sm"
        open={updateDisclosure.isOpen}
        onClose={() => {
          updateDisclosure.onClose();
          setNewEmailError('');
        }}
        onConfirm={() => {
          if (z.string().email().safeParse(newEmail).error) {
            setNewEmailError('Please enter a valid email address');
            return;
          }
          setNewEmailError('');
          updateFurtherUser({
            furtherUserId: furtherUser.furtherUserId,
            email: newEmail,
          });
        }}
        title="Edit user email"
        iconColor="primary"
        icon={Edit}
        content={
          <Spacing size="md">
            <Typography variant="body1">
              The user will need to log in with this new email address to access
              their account. They will not be notified of this change. The
              default emails of any linked investors, advisers or organisation
              users will also be updated.
            </Typography>
            <AppTextInput
              value={newEmail}
              onChange={(e) => {
                setNewEmail(e.target.value);
              }}
              helperText={newEmailError}
              label="New email address"
              name="email"
              fullWidth
            />
          </Spacing>
        }
        btnLabels={{
          cancel: 'Go back',
          confirm: 'Update',
        }}
        confirmBtnProps={{ loading: isUpdating }}
      />
    </>
  );
};

export default ActionsColumn;
